// Municípios e UFs do IBGE: a cidade do perfil fica guardada pelo código de 7 dígitos e aparece como
// "Carapicuíba · SP". A busca aceita texto sem acento e sem maiúsculas. A lista vem de src/dados.ts
// (gerado por scripts/gerar.ts); importe '@truco6/localidades/municipios' para carregar a lista só quando precisar.
import { UFS, type Uf } from './ufs.ts';

export { UFS, type Uf };

export interface Municipio {
  codigo: number; // código IBGE de 7 dígitos
  nome: string;
  uf: Uf;
}

export const SIGLAS: ReadonlySet<string> = new Set(UFS.map((u) => u.sigla));
export const ehUf = (x: unknown): x is Uf => typeof x === 'string' && SIGLAS.has(x);
export const nomeDaUf = (uf: Uf): string => UFS.find((u) => u.sigla === uf)!.nome;

// Sem acento, sem maiúsculas e sem apóstrofo; o resto que não é letra ou número vira espaço.
// A migração do banco usa a mesma regra (public.normalizar_nome).
export function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
    .replace(/['’]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
}

export const rotuloDoMunicipio = (m: Municipio): string => `${m.nome} · ${m.uf}`;

// "1100015;Alta Floresta D'Oeste;RO", uma linha por município.
export function lerMunicipios(dados: string): Municipio[] {
  return dados.trim().split('\n').map((linha) => {
    const [codigo, nome, uf] = linha.split(';');
    if (!codigo || !nome || !ehUf(uf)) throw new Error('linha inválida: ' + linha);
    return { codigo: Number(codigo), nome, uf };
  });
}

export interface Busca {
  lista: readonly Municipio[];
  porCodigo(codigo: number): Municipio | undefined;
  // Primeiro os que começam com o texto, depois os que contêm. Mesmo nome em UFs diferentes sai separado.
  // "campinas sp" ou "Carapicuíba · SP" filtram pela UF.
  buscar(texto: string, limite?: number): Municipio[];
  // Nome que bate com um único município (sem acento e sem maiúsculas), como a migração dos perfis antigos.
  unicoPeloNome(texto: string): Municipio | null;
}

export function criarBusca(lista: readonly Municipio[]): Busca {
  const itens = lista.map((m) => ({ m, n: normalizar(m.nome) }))
    .sort((a, b) => a.n.localeCompare(b.n) || a.m.uf.localeCompare(b.m.uf));
  const codigos = new Map(lista.map((m) => [m.codigo, m]));
  const procurar = (n: string, uf: string | null) => {
    const dela = uf ? itens.filter((i) => i.m.uf === uf) : itens;
    const comeca = dela.filter((i) => i.n.startsWith(n));
    const contem = dela.filter((i) => !i.n.startsWith(n) && i.n.includes(n));
    return [...comeca, ...contem].map((i) => i.m);
  };
  return {
    lista,
    porCodigo: (codigo) => codigos.get(codigo),
    buscar(texto, limite = 8) {
      const n = normalizar(texto);
      if (n.length < 2) return [];
      const fim = /^(.+) ([a-z]{2})$/.exec(n);
      const uf = fim && SIGLAS.has(fim[2]!.toUpperCase()) ? fim[2]!.toUpperCase() : null;
      const achados = uf ? [...procurar(fim![1]!, uf), ...procurar(n, null)] : procurar(n, null);
      return [...new Set(achados)].slice(0, limite);
    },
    unicoPeloNome(texto) {
      const n = normalizar(texto);
      const iguais = n ? itens.filter((i) => i.n === n) : [];
      return iguais.length === 1 ? iguais[0]!.m : null;
    },
  };
}
